import { useState, useRef, useEffect, useCallback } from 'react';

// Constants for resize limits
const MIN_SIDEBAR_WIDTH = 180;
const MAX_SIDEBAR_WIDTH = 600;
const DEFAULT_SIDEBAR_WIDTH = 240;
const SIDEBAR_WIDTH_KEY = 'sidebar-width';

/**
 * Hook for managing sidebar resize functionality
 * Uses direct DOM manipulation during drag for smooth performance
 */
export function useSidebarResize(isCollapsed) {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_SIDEBAR_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef(null);
  const currentWidthRef = useRef(sidebarWidth);

  // Handle resize drag start
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
    currentWidthRef.current = sidebarWidth;
  }, [sidebarWidth]);

  // Double-click the handle to restore the default width
  const handleResizeReset = useCallback(() => {
    currentWidthRef.current = DEFAULT_SIDEBAR_WIDTH;
    setSidebarWidth(DEFAULT_SIDEBAR_WIDTH);
    localStorage.setItem(SIDEBAR_WIDTH_KEY, DEFAULT_SIDEBAR_WIDTH.toString());
    document.documentElement.style.setProperty('--sidebar-width', `${DEFAULT_SIDEBAR_WIDTH}px`);
  }, []);

  // Handle mouse move during resize - direct DOM updates, no React re-renders
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const newWidth = Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, e.clientX));
      currentWidthRef.current = newWidth;

      // Direct DOM manipulation - no React re-render
      if (sidebarRef.current) {
        sidebarRef.current.style.width = `${newWidth}px`;
      }
      document.documentElement.style.setProperty('--sidebar-width', `${newWidth}px`);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      // Now commit to React state (single re-render)
      const finalWidth = currentWidthRef.current;
      setSidebarWidth(finalWidth);
      localStorage.setItem(SIDEBAR_WIDTH_KEY, finalWidth.toString());
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // Change cursor globally during resize
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Set initial CSS variable on mount
  useEffect(() => {
    if (!isCollapsed) {
      document.documentElement.style.setProperty('--sidebar-width', `${sidebarWidth}px`);
    }
  }, [sidebarWidth, isCollapsed]);

  return {
    sidebarWidth,
    isResizing,
    sidebarRef,
    handleResizeStart,
    handleResizeReset,
  };
}

export default useSidebarResize;

